import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import useCustomerInfo from "@repo/shopify/hooks/useCustomerInfo";
import useCustomerInfoCaa from "@repo/shopify/hooks/useCustomerInfoCaa";
import type { ICaaCustomerAddress } from "@repo/shopify/types/api/customer-account";
import type { MailingAddress } from "@repo/shopify/types/generated/storefront.types";

import Button from "@atoms/Button";

import AddressForm from "@organisms/Address/AddressForm";
import AddressFormIntl from "@organisms/Address/AddressFormIntl";
import AddressFormJp from "@organisms/Address/AddressFormJp";
import AddressItem from "@organisms/Address/AddressItem";
import AddressItemJp from "@organisms/Address/AddressItemJp";
import AddressLayout from "@organisms/Address/AddressLayout";

import { URLS } from "@const/urls";

import useAddresses from "@hooks/useAddresses";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export default function Addresses() {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { showFooter } = useLayoutStore();

  const { isPC } = useCheckDevice();

  const [showForm, setShowForm] = useState<boolean>(false);
  const [editableAddress, setEditableAddress] = useState<MailingAddress | ICaaCustomerAddress | undefined>();

  const { onCompletedMutation, defaultAddress } = useAddresses();

  const { customer, caaCustomer } = useCustomerStore();

  const { addresses, isPartnership } = useCustomerInfo(customer, "thisisneverthat");
  const { addresses: caaAddresses } = useCustomerInfoCaa(caaCustomer, "thisisneverthat");

  useEffect(() => {
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (lang === "jp") {
      if (!caaCustomer) navigate(URLS.HOME);
      return;
    }

    if (!customer || isPartnership) navigate(URLS.HOME);
  }, [lang, customer, isPartnership, caaCustomer, navigate]);

  const handleCancelForm = () => {
    setShowForm(false);
    setEditableAddress(undefined);
  };

  const handleFormCompleted = async () => {
    await onCompletedMutation();
    setShowForm(false);
    setEditableAddress(undefined);
  };

  const openAddressForm = (item: MailingAddress | ICaaCustomerAddress | undefined) => {
    setShowForm(true);
    setEditableAddress(item);
  };

  const addressList = useMemo(() => (lang === "jp" ? caaAddresses : addresses), [addresses, caaAddresses, lang]);

  return (
    <>
      <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
        <div className="pc:col-span-3 col-span-8 flex h-fit flex-col gap-5">
          {isPC ? <h2 className="text-title">Addresses</h2> : null}
          <div className="flex flex-col gap-2.5">
            <p className="leading-5 wrap-break-word">
              <Trans i18nKey="address_book.description" />
            </p>
            <Button
              as="square"
              className="w-fit"
              onClick={() => {
                window.scrollTo(0, 0);
                setShowForm(true);
              }}
              variant="secondary"
            >
              {t("address_book.create_new_address")}
            </Button>
          </div>
        </div>

        <div className="pc:col-span-5 col-span-8 grid grid-cols-subgrid">
          {addressList.length ? (
            <ul className="tnt-gap-x pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-5 pt-5">
              {addressList.map((node) => (
                <li
                  className={classNames(
                    "pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-2.5 pb-5",
                    node.id === customer.defaultAddress?.id && "border-grey border-b",
                  )}
                  key={node.id}
                >
                  {lang === "jp" ? (
                    <AddressItemJp
                      address={node as ICaaCustomerAddress}
                      isDefaultAddress={defaultAddress?.id === node.id}
                      onCompletedMutation={onCompletedMutation}
                      openAddressForm={openAddressForm}
                    />
                  ) : (
                    <AddressItem
                      address={node as MailingAddress}
                      isDefaultAddress={defaultAddress?.id === node.id}
                      onCompletedMutation={onCompletedMutation}
                      openAddressForm={openAddressForm}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      {showForm ? (
        <AddressLayout handleCancel={handleCancelForm}>
          <div className="pc:pt-0 scrollbar-hide col-span-8 flex h-[calc(100dvh-10.5rem)] flex-col gap-5 overflow-y-scroll pt-4">
            <div className="flex flex-col gap-2.5">
              <h2 className="text-title">{editableAddress ? t("address_book.edit_address") : t("address_book.create_new_address")}</h2>
            </div>
            {lang === "ko" && (
              <AddressForm
                address={editableAddress as MailingAddress | undefined}
                isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                onClickCancel={handleCancelForm}
                onCompletedMutation={handleFormCompleted}
              />
            )}
            {lang === "en" && (
              <AddressFormIntl
                address={editableAddress as MailingAddress | undefined}
                className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
                isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                isEmptyAddress={!defaultAddress?.id}
                onClickCancel={handleCancelForm}
                onCompletedMutation={handleFormCompleted}
              />
            )}
            {lang === "jp" && (
              <AddressFormJp
                address={editableAddress as ICaaCustomerAddress | undefined}
                className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
                isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                isEmptyAddress={!defaultAddress?.id}
                onClickCancel={handleCancelForm}
                onCompletedMutation={handleFormCompleted}
              />
            )}
          </div>
        </AddressLayout>
      ) : null}
    </>
  );
}
